// src/pages/Auth.jsx
// Production-hardened auth page
// Handles: login, signup (2-step), forgot password, resend confirmation
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import ODFLogo from '../components/ODFLogo';
import { Eye, EyeOff, CheckCircle, Mail, RefreshCw } from 'lucide-react';

const COUNTRIES   = ['United States','United Kingdom','Canada','Australia','Germany','France','Spain','Portugal','Brazil','Nigeria','Ghana','South Africa','Kenya','UAE','Saudi Arabia','India','Singapore','Japan','China','Netherlands','Sweden','Norway','Denmark','Switzerland','Italy','Mexico','Argentina','Colombia','New Zealand','Other'];
const EXPERIENCE  = ['Beginner (< 1 year)','Intermediate (1–3 years)','Advanced (3–5 years)','Professional (5+ years)'];
const MARKETS     = ['Forex','Futures','Crypto','Stocks','Indices','Commodities','Options'];
const RESEND_COOLDOWN = 60; // seconds

const G = '#00e5a0';

const inp = {
  width:'100%', background:'#0a0a0f', border:'1px solid #1e1e2e',
  borderRadius:8, color:'#f0f0f5', padding:'12px 14px', fontSize:14,
  fontFamily:"'IBM Plex Sans',sans-serif", outline:'none',
  boxSizing:'border-box', transition:'border-color 0.2s',
};
const fo = e => e.target.style.borderColor = G;
const fb = e => e.target.style.borderColor = '#1e1e2e';
const Lbl = ({ children }) => <label style={{ fontSize:11, color:'#8888a8', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.07em', display:'block', marginBottom:6 }}>{children}</label>;
const Err = ({ msg }) => msg ? (
  <div style={{ fontSize:12, color:'#ff4d6d', background:'rgba(255,77,109,0.08)', border:'1px solid rgba(255,77,109,0.2)', borderRadius:7, padding:'10px 12px', lineHeight:1.5 }}>{msg}</div>
) : null;
const pwColors = ['#1e1e2e','#ff4d6d','#ffd166','#00b87d','#00e5a0'];
const pwLabels = ['','Too short','Weak','Good','Strong'];
const card = { background:'#16161f', border:'1px solid #1e1e2e', borderRadius:16, padding:'36px 32px', width:'100%' };

function SubmitBtn({ children, disabled, loading }) {
  return (
    <button type="submit" disabled={disabled || loading} style={{
      width:'100%', background:G, color:'#0a0a0f', border:'none', borderRadius:8,
      padding:'13px', fontSize:14, fontWeight:700,
      cursor:(disabled || loading) ? 'not-allowed' : 'pointer',
      fontFamily:"'IBM Plex Sans',sans-serif",
      opacity:(disabled || loading) ? 0.55 : 1, transition:'all 0.2s'
    }}>
      {loading ? (
        <span style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
          <span style={{ width:14, height:14, borderRadius:'50%', border:'2px solid rgba(0,0,0,0.3)', borderTopColor:'#0a0a0f', display:'inline-block', animation:'spin 0.7s linear infinite' }} />
          Please wait...
        </span>
      ) : children}
    </button>
  );
}


export default function Auth({ onBack }) {
  const { signIn, signUp, resendConfirmation } = useAuth();

  const [mode,       setMode]       = useState('login');
  const [step,       setStep]       = useState(1);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const [forgot,     setForgot]     = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [showPw,     setShowPw]     = useState(false);
  const [agreed,     setAgreed]     = useState(false);
  const [signupDone, setSignupDone] = useState(false);
  const [signupEmail,setSignupEmail]= useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendLoading,  setResendLoading]  = useState(false);
  const [resendSuccess,  setResendSuccess]  = useState(false);
  const cooldownRef = useRef(null);

  // Step 1 fields
  const [name,    setName]    = useState('');
  const [email,   setEmail]   = useState('');
  const [pw,      setPw]      = useState('');
  const [confirm, setConfirm] = useState('');
  // Step 2 fields
  const [username,   setUsername]   = useState('');
  const [country,    setCountry]    = useState('');
  const [experience, setExperience] = useState('');
  const [markets,    setMarkets]    = useState([]);
  // broker removed from signup

  // Cooldown timer for resend
  useEffect(() => {
    if (resendCooldown > 0) {
      cooldownRef.current = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    }
    return () => clearTimeout(cooldownRef.current);
  }, [resendCooldown]);

  const toggleMarket = m => setMarkets(p => p.includes(m) ? p.filter(x => x !== m) : [...p, m]);

  const clearForm = () => {
    setError(''); setName(''); setEmail(''); setPw(''); setConfirm('');
    setAgreed(false); setForgot(false); setForgotSent(false); setStep(1);
    setResendCooldown(0); setResendSuccess(false);
  };

  const switchMode = m => { setMode(m); clearForm(); };

  // Friendly error messages — never show raw Supabase errors
  const friendlyError = (msg = '') => {
    if (msg.includes('Email not confirmed'))   return 'Please confirm your email first. Check your inbox.';
    if (msg.includes('invalid_credentials') || msg.includes('Invalid login')) return 'Incorrect email or password.';
    if (msg.includes('already registered') || msg.includes('User already')) return 'An account with this email already exists. Try signing in.';
    if (msg.includes('Password should'))       return 'Password must be at least 6 characters.';
    if (msg.includes('network') || msg.includes('Network')) return 'Connection error. Please check your internet and try again.';
    if (msg.includes('rate limit') || msg.includes('too many')) return 'Too many attempts. Please wait a moment and try again.';
    return msg || 'Something went wrong. Please try again.';
  };

  // LOGIN
  const handleLogin = async e => {
    e.preventDefault();
    if (loading) return;
    setError(''); setLoading(true);
    const { error } = await signIn(email.trim().toLowerCase(), pw);
    setLoading(false);
    if (error) setError(friendlyError(error.message));
  };

  // SIGNUP STEP 1
  const handleStep1 = e => {
    e.preventDefault();
    setError('');
    if (!name.trim())       { setError('Please enter your full name.'); return; }
    if (!email.trim())      { setError('Please enter your email.'); return; }
    if (pw.length < 6)      { setError('Password must be at least 6 characters.'); return; }
    if (pw !== confirm)     { setError('Passwords do not match.'); return; }
    if (!agreed)            { setError('Please agree to the Terms of Service, Privacy Policy, and Risk Disclaimer.'); return; }
    const base = name.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');
    setUsername(base + Math.floor(Math.random() * 99));
    setStep(2);
  };

  // SIGNUP STEP 2
  const handleStep2 = async e => {
    e.preventDefault();
    if (loading) return;
    setError(''); setLoading(true);
    const { data, error } = await signUp(email.trim().toLowerCase(), pw);
    if (error) { setError(friendlyError(error.message)); setLoading(false); return; }
    // Save profile — don't block signup if this fails
    if (data?.user) {
      try {
        await supabase.from('profiles').upsert({
          id: data.user.id, email: email.trim().toLowerCase(),
          full_name: name.trim(),
          username: username || name.toLowerCase().replace(/\s+/g, ''),
          country, experience,
          markets: markets.join(', '),
          });
      } catch (profileErr) {
        console.warn('Profile save failed (non-critical):', profileErr);
      }
    }
    setSignupEmail(email.trim().toLowerCase());
    setSignupDone(true);
    setResendCooldown(RESEND_COOLDOWN);
    setLoading(false);
  };

  // FORGOT PASSWORD
  const handleForgot = async e => {
    e.preventDefault();
    if (loading) return;
    setError(''); setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
      redirectTo: `${window.location.origin}?type=recovery`,
    });
    setLoading(false);
    if (error) setError(friendlyError(error.message));
    else setForgotSent(true);
  };

  // RESEND CONFIRMATION EMAIL
  const handleResend = async () => {
    if (resendCooldown > 0 || resendLoading) return;
    setResendLoading(true); setResendSuccess(false);
    const { error } = await resendConfirmation(signupEmail);
    setResendLoading(false);
    if (!error) {
      setResendSuccess(true);
      setResendCooldown(RESEND_COOLDOWN);
    }
  };

  const pwStr = pw.length === 0 ? 0 : pw.length < 4 ? 1 : pw.length < 7 ? 2 : pw.length < 10 ? 3 : 4;

  return (
    <div style={{ minHeight:'100vh', background:'#0a0a0f', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:24, fontFamily:"'IBM Plex Sans',sans-serif", position:'relative' }}>
      <div style={{ position:'absolute', top:'30%', left:'50%', transform:'translate(-50%,-50%)', width:500, height:500, background:'radial-gradient(circle, rgba(0,229,160,0.05) 0%, transparent 70%)', pointerEvents:'none' }} />

      <button onClick={onBack} style={{ position:'absolute', top:24, left:24, background:'none', border:'none', cursor:'pointer', color:'#55556a', fontSize:13, display:'flex', alignItems:'center', gap:6, fontFamily:"'IBM Plex Sans',sans-serif", padding:0, transition:'color 0.2s' }}
        onMouseEnter={e=>e.currentTarget.style.color='#f0f0f5'} onMouseLeave={e=>e.currentTarget.style.color='#55556a'}>
        ← Back to home
      </button>

      <div style={{ marginBottom:28 }}><ODFLogo size="sidebar" /></div>

      {/* ── EMAIL CONFIRMATION SENT ── */}
      {signupDone ? (
        <div style={{ ...card, maxWidth:440, textAlign:'center' }}>
          <div style={{ width:64, height:64, borderRadius:'50%', background:'rgba(0,229,160,0.1)', border:'1px solid rgba(0,229,160,0.25)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
            <Mail size={28} style={{ color:G }} />
          </div>
          <h2 style={{ fontSize:20, fontWeight:700, color:'#f0f0f5', margin:'0 0 10px', letterSpacing:'-0.01em' }}>Check your inbox</h2>
          <p style={{ fontSize:14, color:'#8888a8', lineHeight:1.8, margin:'0 0 20px' }}>
            We sent a confirmation link to<br/><strong style={{ color:'#f0f0f5' }}>{signupEmail}</strong>
          </p>
          <div style={{ background:'#0a0a0f', border:'1px solid #1e1e2e', borderRadius:10, padding:'16px 20px', marginBottom:20, textAlign:'left' }}>
            <div style={{ fontSize:12, color:'#8888a8', lineHeight:2 }}>
              <div>1. Open the email from <span style={{ color:G }}>info@odftrades.com</span></div>
              <div>2. Click <strong style={{ color:'#f0f0f5' }}>Confirm Email Address</strong></div>
              <div>3. You'll land straight on your dashboard</div>
            </div>
          </div>

          {/* Resend button with cooldown */}
          <div style={{ marginBottom:16 }}>
            {resendSuccess && (
              <div style={{ fontSize:12, color:G, marginBottom:10, display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                <CheckCircle size={14} /> Email resent successfully
              </div>
            )}
            <button onClick={handleResend} disabled={resendCooldown > 0 || resendLoading} style={{
              width:'100%', background:'transparent',
              border:`1px solid ${resendCooldown > 0 ? '#1e1e2e' : 'rgba(0,229,160,0.3)'}`,
              borderRadius:8, padding:'11px', fontSize:13,
              color: resendCooldown > 0 ? '#55556a' : G,
              cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer',
              fontFamily:"'IBM Plex Sans',sans-serif", fontWeight:500,
              display:'flex', alignItems:'center', justifyContent:'center', gap:8,
              transition:'all 0.2s',
            }}>
              {resendLoading ? (
                <><span style={{ width:12, height:12, borderRadius:'50%', border:'2px solid rgba(0,229,160,0.3)', borderTopColor:G, display:'inline-block', animation:'spin 0.7s linear infinite' }} /> Sending...</>
              ) : resendCooldown > 0 ? (
                `Resend in ${resendCooldown}s`
              ) : (
                <><RefreshCw size={13} /> Resend confirmation email</>
              )}
            </button>
          </div>

          <p style={{ fontSize:11, color:'#55556a', margin:'0 0 16px', lineHeight:1.7 }}>
            Can't find it? Check your spam or junk folder.
          </p>
          <button onClick={() => { setSignupDone(false); switchMode('login'); }} style={{
            width:'100%', background:'transparent', color:G,
            border:`1px solid rgba(0,229,160,0.3)`, borderRadius:8,
            padding:'11px', fontSize:13, fontWeight:600,
            cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif",
          }}>
            Go to Sign In →
          </button>
        </div>

      ) : forgot ? (
        /* ── FORGOT PASSWORD ── */
        <div style={{ ...card, maxWidth:400 }}>
          {forgotSent ? (
            <div style={{ textAlign:'center' }}>
              <div style={{ width:56, height:56, borderRadius:'50%', background:'rgba(0,229,160,0.1)', border:'1px solid rgba(0,229,160,0.25)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
                <Mail size={24} style={{ color:G }} />
              </div>
              <h2 style={{ fontSize:18, fontWeight:700, color:'#f0f0f5', margin:'0 0 10px' }}>Reset link sent</h2>
              <p style={{ fontSize:13, color:'#8888a8', lineHeight:1.8, margin:'0 0 8px' }}>
                Check <strong style={{ color:'#f0f0f5' }}>{email}</strong> for your reset link.
              </p>
              <p style={{ fontSize:11, color:'#55556a', margin:'0 0 24px' }}>
                Link expires in 1 hour. Check spam if needed.
              </p>
              <button onClick={() => { setForgot(false); setForgotSent(false); setError(''); }} style={{
                width:'100%', background:G, color:'#0a0a0f', border:'none',
                borderRadius:8, padding:'12px', fontSize:13, fontWeight:700,
                cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif",
              }}>Back to Sign In</button>
            </div>
          ) : (
            <form onSubmit={handleForgot} style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div>
                <h2 style={{ fontSize:18, fontWeight:700, color:'#f0f0f5', margin:'0 0 6px' }}>Forgot password?</h2>
                <p style={{ fontSize:13, color:'#8888a8', margin:0, lineHeight:1.6 }}>Enter your email and we'll send a reset link.</p>
              </div>
              <div><Lbl>Email</Lbl><input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="your@email.com" required style={inp} onFocus={fo} onBlur={fb} /></div>
              <Err msg={error} />
              <SubmitBtn loading={loading}>Send Reset Link →</SubmitBtn>
              <button type="button" onClick={() => { setForgot(false); setError(''); }} style={{ background:'none', border:'none', cursor:'pointer', color:'#55556a', fontSize:12, fontFamily:"'IBM Plex Sans',sans-serif", textAlign:'center', marginTop:4 }}>
                ← Back to sign in
              </button>
            </form>
          )}
        </div>

      ) : (
        <div style={{ ...card, maxWidth: mode === 'signup' && step === 2 ? 520 : 400 }}>
          {/* Tabs */}
          <div style={{ display:'flex', background:'#0a0a0f', borderRadius:10, padding:4, marginBottom:28, border:'1px solid #1e1e2e' }}>
            {[['login','Sign In'],['signup','Create Account']].map(([m,l]) => (
              <button key={m} onClick={() => switchMode(m)} style={{
                flex:1, padding:'9px', borderRadius:7, border:'none',
                background:mode===m?'#16161f':'transparent',
                color:mode===m?'#f0f0f5':'#55556a',
                fontSize:13, fontWeight:mode===m?600:400,
                cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif",
                transition:'all 0.2s',
                boxShadow:mode===m?'0 1px 4px rgba(0,0,0,0.3)':'none'
              }}>{l}</button>
            ))}
          </div>

          {/* ── LOGIN ── */}
          {mode === 'login' && (
            <form onSubmit={handleLogin} style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div><Lbl>Email</Lbl><input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="your@email.com" required autoComplete="email" style={inp} onFocus={fo} onBlur={fb} /></div>
              <div>
                <Lbl>Password</Lbl>
                <div style={{ position:'relative' }}>
                  <input type={showPw?'text':'password'} value={pw} onChange={e=>setPw(e.target.value)} placeholder="••••••••" required autoComplete="current-password" style={{ ...inp, paddingRight:44 }} onFocus={fo} onBlur={fb} />
                  <button type="button" onClick={() => setShowPw(s=>!s)} style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#55556a', padding:0, lineHeight:0 }}>
                    {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
                  </button>
                </div>
              </div>
              <button type="button" onClick={() => { setForgot(true); setError(''); }} style={{ background:'none', border:'none', cursor:'pointer', color:'#55556a', fontSize:12, textAlign:'right', fontFamily:"'IBM Plex Sans',sans-serif", padding:0, marginTop:-6, transition:'color 0.2s' }}
                onMouseEnter={e=>e.target.style.color=G} onMouseLeave={e=>e.target.style.color='#55556a'}>
                Forgot password?
              </button>
              <Err msg={error} />
              <SubmitBtn loading={loading}>Sign In →</SubmitBtn>
              <div style={{ textAlign:'center', fontSize:13, color:'#55556a' }}>
                Don't have an account?{' '}
                <button type="button" onClick={() => switchMode('signup')} style={{ background:'none', border:'none', color:G, cursor:'pointer', fontSize:13, fontFamily:"'IBM Plex Sans',sans-serif", fontWeight:600, padding:0 }}>
                  Create Account →
                </button>
              </div>
            </form>
          )}

          {/* ── SIGNUP STEP 1 ── */}
          {mode === 'signup' && step === 1 && (
            <form onSubmit={handleStep1} style={{ display:'flex', flexDirection:'column', gap:14 }}>
              {/* Progress */}
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                <div style={{ display:'flex', gap:4 }}>
                  {[1,2].map(s=><div key={s} style={{ width:28, height:3, borderRadius:2, background:step>=s?G:'#1e1e2e', transition:'background 0.3s' }} />)}
                </div>
                <span style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace" }}>Step 1 of 2</span>
              </div>

              <div><Lbl>Full Name</Lbl><input type="text" value={name} onChange={e=>setName(e.target.value)} placeholder="Your name" required autoComplete="name" style={inp} onFocus={fo} onBlur={fb} /></div>
              <div><Lbl>Email</Lbl><input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="your@email.com" required autoComplete="email" style={inp} onFocus={fo} onBlur={fb} /></div>
              <div>
                <Lbl>Password</Lbl>
                <div style={{ position:'relative' }}>
                  <input type={showPw?'text':'password'} value={pw} onChange={e=>setPw(e.target.value)} placeholder="Min. 6 characters" required autoComplete="new-password" style={{ ...inp, paddingRight:44 }} onFocus={fo} onBlur={fb} />
                  <button type="button" onClick={() => setShowPw(s=>!s)} style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#55556a', padding:0, lineHeight:0 }}>
                    {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
                  </button>
                </div>
                {pw.length > 0 && (
                  <div style={{ marginTop:6 }}>
                    <div style={{ display:'flex', gap:4, marginBottom:3 }}>
                      {[1,2,3,4].map(i=><div key={i} style={{ flex:1, height:3, borderRadius:2, background:pwStr>=i?pwColors[pwStr]:'#1e1e2e', transition:'background 0.2s' }} />)}
                    </div>
                    <div style={{ fontSize:10, color:pwColors[pwStr], fontFamily:"'DM Mono',monospace" }}>{pwLabels[pwStr]}</div>
                  </div>
                )}
              </div>
              <div>
                <Lbl>Confirm Password</Lbl>
                <input type="password" value={confirm} onChange={e=>setConfirm(e.target.value)} placeholder="••••••••" required autoComplete="new-password" style={inp} onFocus={fo} onBlur={fb} />
                {confirm.length > 0 && (
                  <div style={{ fontSize:10, marginTop:4, fontFamily:"'DM Mono',monospace", color: pw===confirm ? G : '#ff4d6d' }}>
                    {pw===confirm ? '✓ Passwords match' : '✗ Passwords do not match'}
                  </div>
                )}
              </div>

              {/* Terms — links open the real Legal pages in a new
                  tab so the sign-up form keeps its state. stopPropagation
                  on link clicks prevents the row's checkbox toggle. */}
              <div style={{ display:'flex', alignItems:'flex-start', gap:10, padding:12, background:'rgba(255,255,255,0.02)', border:`1px solid ${agreed?'rgba(0,229,160,0.2)':'#1e1e2e'}`, borderRadius:8, cursor:'pointer', transition:'border-color 0.2s', userSelect:'none' }}
                onClick={() => setAgreed(a=>!a)}>
                <div style={{ width:16, height:16, borderRadius:4, border:`2px solid ${agreed?G:'#2a2a3e'}`, background:agreed?G:'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:1, transition:'all 0.15s' }}>
                  {agreed && <CheckCircle size={10} style={{ color:'#0a0a0f' }} />}
                </div>
                <span style={{ fontSize:12, color:'#8888a8', lineHeight:1.6, cursor:'pointer' }}>
                  I agree to the ODFTrades{' '}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e=>e.stopPropagation()}
                    style={{ color:G, textDecoration:'none', fontWeight:500 }}
                  >
                    Terms of Service
                  </a>
                  ,{' '}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e=>e.stopPropagation()}
                    style={{ color:G, textDecoration:'none', fontWeight:500 }}
                  >
                    Privacy Policy
                  </a>
                  , and{' '}
                  <a
                    href="/disclaimer"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e=>e.stopPropagation()}
                    style={{ color:G, textDecoration:'none', fontWeight:500 }}
                  >
                    Risk Disclaimer
                  </a>
                  . I understand that ODFTrades is an educational trading platform and software company,
                  that trading involves substantial risk, and that nothing on ODFTrades is financial,
                  investment, legal, or tax advice.
                </span>
              </div>

              <Err msg={error} />
              <SubmitBtn loading={loading} disabled={!agreed}>Continue →</SubmitBtn>
              <div style={{ textAlign:'center', fontSize:13, color:'#55556a' }}>
                Already have an account?{' '}
                <button type="button" onClick={() => switchMode('login')} style={{ background:'none', border:'none', color:G, cursor:'pointer', fontSize:13, fontFamily:"'IBM Plex Sans',sans-serif", fontWeight:600, padding:0 }}>
                  Sign In →
                </button>
              </div>
            </form>
          )}

          {/* ── SIGNUP STEP 2 ── */}
          {mode === 'signup' && step === 2 && (
            <form onSubmit={handleStep2} style={{ display:'flex', flexDirection:'column', gap:16 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                <button type="button" onClick={() => setStep(1)} style={{ background:'none', border:'none', cursor:'pointer', color:'#55556a', fontSize:12, fontFamily:"'IBM Plex Sans',sans-serif", padding:0 }}>← Back</button>
                <div style={{ display:'flex', gap:4 }}>
                  {[1,2].map(s=><div key={s} style={{ width:28, height:3, borderRadius:2, background:step>=s?G:'#1e1e2e' }} />)}
                </div>
                <span style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace" }}>Step 2 of 2 — Trading profile</span>
              </div>

              <div>
                <Lbl>Username</Lbl>
                <input type="text" value={username} onChange={e=>setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9]/g,''))} placeholder="e.g. odftrader" style={inp} onFocus={fo} onBlur={fb} />
                <div style={{ fontSize:11, color:'#55556a', marginTop:4, fontFamily:"'DM Mono',monospace" }}>odftrades.com/u/{username||'yourname'}</div>
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div><Lbl>Country</Lbl><select value={country} onChange={e=>setCountry(e.target.value)} style={{ ...inp, appearance:'none' }}><option value="">— Select —</option>{COUNTRIES.map(c=><option key={c}>{c}</option>)}</select></div>
                <div><Lbl>Experience</Lbl><select value={experience} onChange={e=>setExperience(e.target.value)} style={{ ...inp, appearance:'none' }}><option value="">— Select —</option>{EXPERIENCE.map(x=><option key={x}>{x}</option>)}</select></div>
              </div>

              <div>
                <Lbl>Markets You Trade</Lbl>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:4 }}>
                  {MARKETS.map(m=>(
                    <button key={m} type="button" onClick={()=>toggleMarket(m)} style={{ padding:'6px 12px', borderRadius:7, border:'1px solid', fontSize:12, cursor:'pointer', outline:'none', fontFamily:"'IBM Plex Sans',sans-serif", background:markets.includes(m)?'rgba(0,229,160,0.1)':'transparent', borderColor:markets.includes(m)?'rgba(0,229,160,0.3)':'#1e1e2e', color:markets.includes(m)?G:'#8888a8', transition:'all 0.15s' }}>{m}</button>
                  ))}
                </div>
              </div>

              <div><Lbl>Primary Broker / Platform</Lbl><input type="text" value={broker} onChange={e=>setBroker(e.target.value)} placeholder="e.g. MT5, FTMO, Interactive Brokers" style={inp} onFocus={fo} onBlur={fb} /></div>

              <Err msg={error} />
              <SubmitBtn loading={loading}>Create Account →</SubmitBtn>
              <div style={{ fontSize:11, color:'#2a2a3e', textAlign:'center' }}>Step 2 is optional — update anytime from Settings</div>
            </form>
          )}
        </div>
      )}

      <div style={{ fontSize:11, color:'#2a2a3e', marginTop:24, fontFamily:"'DM Mono',monospace" }}>
        ODFTrades · Your data is private and secure
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
